/**
 * domains/acquiring/api.ts — Чтение настроек эквайринга компании.
 *
 * 1.27.0 — P0-3a:
 *   • fetchAcquiringSettings(companyId) — читает публичные поля из БД,
 *     кэширует в Dexie. Офлайн-first.
 *   • computeAcquiringAvailability(...) — чистая функция, считает,
 *     можно ли сейчас показать кнопку онлайн-оплаты.
 *
 * В этом релизе НЕТ функций активации/деактивации/ввода credentials —
 * они в P0-3b. Сейчас таблица заполняется только из Supabase Dashboard
 * вручную (для тестов суперадмина) либо позже через UI в P0-3b.
 */

import { isSupabaseConfigured, supabase } from '../auth/supabaseClient';
import { db } from '../sync/db';
import type {
  CompanyAcquiringSettingsPublic,
  AcquiringAvailability,
} from './types';

// ─── Mapping ─────────────────────────────────────────────────────────────────

function mapRow(row: Record<string, unknown>): CompanyAcquiringSettingsPublic {
  return {
    id:                       row.id as string,
    company_id:               row.company_id as string,
    provider:                 row.provider as 'yookassa',
    shop_id:                  (row.shop_id as string) ?? null,
    status:                   row.status as CompanyAcquiringSettingsPublic['status'],
    verified_at:              (row.verified_at as string) ?? null,
    commission_rate_override: row.commission_rate_override as number | null,
    suspended_reason:         (row.suspended_reason as string) ?? null,
    suspended_at:             (row.suspended_at as string) ?? null,
    webhook_url:              (row.webhook_url as string) ?? null,
    created_at:               row.created_at as string,
    updated_at:               row.updated_at as string,
  };
}

// ─── Fetch ───────────────────────────────────────────────────────────────────

/**
 * Читает acquiring settings компании.
 *
 * Стратегия (как и в fetchPublicCompanyInfo):
 *   1. Сначала из Dexie (мгновенный ответ, работает офлайн).
 *   2. Параллельно — refresh с сервера, обновляет кэш.
 *   3. Если в Dexie ничего нет и сети нет — null.
 */
export async function fetchAcquiringSettings(
  companyId: string,
): Promise<CompanyAcquiringSettingsPublic | null> {
  if (!companyId) return null;

  const cached = await db.acquiring_settings.get(companyId).catch(() => null);

  // Фоновое обновление из сети (не блокирует return)
  if (isSupabaseConfigured && supabase && navigator.onLine) {
    void refreshAcquiringSettingsFromServer(companyId);
  }

  return cached ?? null;
}

/**
 * Принудительное обновление из сети + запись в Dexie.
 * Безопасна к ошибкам — никогда не бросает.
 */
export async function refreshAcquiringSettingsFromServer(
  companyId: string,
): Promise<CompanyAcquiringSettingsPublic | null> {
  if (!isSupabaseConfigured || !supabase) return null;
  try {
    // ВАЖНО: НЕ запрашиваем secret_encrypted и secret_encryption_iv.
    // На клиенте они не должны существовать никогда. RLS их не вернёт
    // authenticated-юзеру всё равно, но мы дополнительно фильтруем здесь.
    const { data, error } = await supabase
      .from('company_acquiring_settings')
      .select(
        'id, company_id, provider, shop_id, status, verified_at, commission_rate_override, suspended_reason, suspended_at, webhook_url, created_at, updated_at',
      )
      .eq('company_id', companyId)
      .maybeSingle();

    if (error || !data) return null;
    const settings = mapRow(data as Record<string, unknown>);
    await db.acquiring_settings.put(settings).catch(() => {});
    return settings;
  } catch {
    return null;
  }
}

/**
 * Hydration при логине — аналогично hydrateCompanyInfo.
 * Безопасна к ошибкам.
 */
export async function hydrateAcquiringSettings(companyId: string): Promise<void> {
  if (!companyId) return;
  await refreshAcquiringSettingsFromServer(companyId);
}

// ─── Availability computation ────────────────────────────────────────────────

/**
 * Чистая функция: можно ли сейчас принять онлайн-оплату.
 *
 * Все условия должны быть true:
 *   1. У компании есть запись acquiring_settings со status='active'.
 *   2. Тариф компании включает feature 'online_payments'.
 *   3. Есть сеть (navigator.onLine === true).
 *
 * Возвращает развёрнутый объект чтобы UI мог точно показать, почему
 * кнопка скрыта (например, для подсказки админу).
 */
export function computeAcquiringAvailability(input: {
  settings: CompanyAcquiringSettingsPublic | null;
  hasOnlinePaymentsFeature: boolean;
  isOnline: boolean;
}): AcquiringAvailability {
  if (!input.hasOnlinePaymentsFeature) {
    return { canAcceptOnlinePayment: false, reason: 'feature_not_in_plan' };
  }
  if (!input.isOnline) {
    return { canAcceptOnlinePayment: false, reason: 'offline' };
  }
  if (!input.settings) {
    return { canAcceptOnlinePayment: false, reason: 'no_settings' };
  }
  switch (input.settings.status) {
    case 'active':
      return { canAcceptOnlinePayment: true, reason: 'available' };
    case 'inactive':
      return { canAcceptOnlinePayment: false, reason: 'inactive' };
    case 'pending_verification':
      return { canAcceptOnlinePayment: false, reason: 'pending_verification' };
    case 'suspended':
      return {
        canAcceptOnlinePayment: false,
        reason: 'suspended',
        suspended_reason: input.settings.suspended_reason,
      };
    default:
      return { canAcceptOnlinePayment: false, reason: 'inactive' };
  }
}

// ─── 1.28.0 — P0-3b: superadmin actions ──────────────────────────────────────
//
// Эти функции вызывают Edge Functions с правильной обработкой non-2xx
// (см. урок из P0-2 — supabase-js завернёт 503/4xx в FunctionsHttpError
// и тело попадёт в error.context, а не в data).
//
// Только для пользователей с platform_admin/platform_owner. RLS на самой
// таблице не позволит другим даже SELECT секретные поля, но дополнительная
// проверка на server-side (в Edge Function) — это вторая линия обороны.

export interface VerifyCredentialsInput {
  company_id: string;
  shop_id: string;
  secret_key: string;
  commission_rate_override?: number | null;
}

export interface VerifyCredentialsResponse {
  success: boolean;
  status?: string;
  shop_id_masked?: string;
  verified_at?: string;
  webhook_url?: string;
  error?: string;
}

/**
 * Активирует / переактивирует эквайринг компании.
 *
 * Серверный flow:
 *   1. Проверка прав caller (platform_admin/owner).
 *   2. Проверка тарифа компании (commission_rate != null).
 *   3. Тестовый GET /payments?limit=1 к ЮKassa — проверка credentials.
 *   4. AES-GCM шифрование secret_key.
 *   5. Upsert в company_acquiring_settings, status='active'.
 *   6. Audit log с маской ключа.
 */
export async function activateAcquiring(
  input: VerifyCredentialsInput,
): Promise<VerifyCredentialsResponse> {
  if (!isSupabaseConfigured || !supabase) {
    throw new Error('Supabase не настроен');
  }

  const { data, error } = await supabase.functions.invoke('verify-acquiring-credentials', {
    body: input,
  });

  // Урок из P0-2: разворачиваем error.context при non-2xx
  if (error) {
    let serverBody: VerifyCredentialsResponse | null = null;
    const ctx = (error as unknown as { context?: Response }).context;
    if (ctx && typeof ctx.json === 'function') {
      try {
        serverBody = await ctx.clone().json();
      } catch {
        serverBody = null;
      }
    }
    throw new Error(serverBody?.error ?? error.message ?? 'Не удалось активировать эквайринг');
  }

  const response = data as VerifyCredentialsResponse;
  if (!response.success) {
    throw new Error(response.error ?? 'Не удалось активировать эквайринг');
  }

  return response;
}

export interface SuspendAcquiringInput {
  company_id: string;
  action: 'suspend' | 'reactivate';
  reason?: string;
}

export interface SuspendAcquiringResponse {
  success: boolean;
  status?: string;
  error?: string;
}

/**
 * Ручная приостановка или восстановление эквайринга суперадмином.
 *
 * Reactivate работает только если у компании уже есть verified_at
 * (т.е. credentials когда-то проверялись). Иначе нужно использовать
 * activateAcquiring с заново введёнными credentials.
 */
export async function suspendAcquiring(
  input: SuspendAcquiringInput,
): Promise<SuspendAcquiringResponse> {
  if (!isSupabaseConfigured || !supabase) {
    throw new Error('Supabase не настроен');
  }

  const { data, error } = await supabase.functions.invoke('suspend-acquiring', {
    body: input,
  });

  if (error) {
    let serverBody: SuspendAcquiringResponse | null = null;
    const ctx = (error as unknown as { context?: Response }).context;
    if (ctx && typeof ctx.json === 'function') {
      try {
        serverBody = await ctx.clone().json();
      } catch {
        serverBody = null;
      }
    }
    throw new Error(serverBody?.error ?? error.message ?? 'Не удалось изменить статус эквайринга');
  }

  const response = data as SuspendAcquiringResponse;
  if (!response.success) {
    throw new Error(response.error ?? 'Не удалось изменить статус эквайринга');
  }

  return response;
}
